import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { notify } from '../utils/notify'
import { Form, Input, Button, Select, Checkbox, Spin, Modal, Upload, Card, Row, Col, InputNumber } from 'antd'
import { UploadOutlined } from '@ant-design/icons'
import { validateForm } from '../utils/validateForms'
import { getProviderById, saveClinicianData, getAMDColumns, getClinicianAppointmentTypes, saveClinicianEmail } from '../services/Clinician2Service'
import { findOrCreateGHLContact, findOrCreateGHLUser, findOrCreateGHLCalendar, completeClinicianOnboarding } from '../services/GHLService'
import { generateRandomPassword } from '../utils/passwordUtils'
import TextArea from 'antd/es/input/TextArea'
import dropdownOptions from '../assets/json/DropdownOptions.json'
import { convertToTitleCase } from '../utils/stringUtils'
import { categorizeAppointment } from '../utils/categorizeAppointment'
const { Option } = Select

const CompleteClinicianOnboarding = () => {
  const [form] = Form.useForm()
  const navigate = useNavigate()
  const location = useLocation()
  const [loading, setLoading] = useState(false)
  const [imageUrl, setImageUrl] = useState(null)
  const [clinicianId, setClinicianId] = useState(null)
  const [clinicianName, setClinicianName] = useState('')
  const [amdColumns, setAmdColumns] = useState([])
  const [fetchingAmdColumns, setFetchingAmdColumns] = useState(false)
  const [clinicianData, setClinicianData] = useState({})
  const [isAvailableForScheduling, setIsAvailableForScheduling] = useState(false)
  const [fileList, setFileList] = useState([])
  const [imageLoading, setImageLoading] = useState(true)
  const [appointmentsMap, setAppointmentsMap] = useState({})

  const appointmentOptions = [
    'Medication',
    'Therapy',
    'Therapy Family',
    'Therapy Group',
    'Therapy Couple',
    'INMHP',
    'Coach',
    'Coach Peer Group',
    'Coach Group',
    'MD/NP/PA',
    'BHI',
    'Preventative E/M'
  ]

  useEffect(() => {
    // Extract clinician ID from URL query parameters
    const queryParams = new URLSearchParams(location.search)
    const id = queryParams.get('id')
    if (id) {
      setClinicianId(id)
      loadClinicianData(id)
    }

    // Fetch AMD columns
    fetchAmdColumns()
  }, [location])

  const fetchAmdColumns = async () => {
    setFetchingAmdColumns(true)
    try {
      // Use the service method instead of direct fetch
      const data = await getAMDColumns();
      
      // Validate that data has the expected format
      if (Array.isArray(data)) {
        setAmdColumns(data)
      } else {
        console.error('Unexpected response format from AMD clinicians API:', data)
        setAmdColumns([])
      }
    } catch (error) {
      console.error('Error fetching AMD clinicians:', error)
      
      // Check for authentication error
      if (error.response && (error.response.status === 401 || error.response.status === 403)) {
        Modal.error({
          title: 'Authentication Error',
          content: 'Your session may have expired. Please log in again to continue.'
        })
      } else {
        Modal.error({
          title: 'Error',
          content: 'Failed to load AMD clinicians data. Please try again later.'
        })
      }
      setAmdColumns([])
    } finally {
      setFetchingAmdColumns(false)
    }
  }

  const loadClinicianData = async (id) => {
    setLoading(true)
    try {
      const provider = await getProviderById(id)
      setClinicianData(provider)

      // Set the clinician name for display
      setClinicianName(
        provider.first_name && provider.last_name
        ? `${convertToTitleCase(provider.first_name)} ${convertToTitleCase(provider.last_name)}`
        : provider.email || 'Clinician'
      )

      // Set form fields with existing data
      form.setFieldsValue({
        provider_bio: provider.provider_bio || '',
        provider_title: provider.provider_title || '',
        doxy_link: provider.doxy_link || '',
        timezone: provider.timezone || '',
        search_priority: provider.search_priority || 0,
        available_for_scheduling: provider.available_for_scheduling || false,
        available_for_new_booking: provider.available_for_new_booking || false,
        available_for_followup_booking: provider.available_for_followup_booking || false,
        // Set AMD fields if they exist
        amd_column_id: provider.provider_integration_map?.amd_column_id || '',
        amd_column_heading: provider.provider_integration_map?.amd_column_heading || '',
        // If we have AMD column ID, set the amd_details field to match for the dropdown
        amd_details: Number(provider.provider_integration_map?.amd_column_id) || null,
        lunajoy_email: provider.email || ''
      })

      const allAppointmentTypes = await getClinicianAppointmentTypes()
      // get an object mappping appointment id and appointment name. e.g.:- {1 : "med new", 2 : "med f/u", ...}
      const appointmentMap = {}
  
      allAppointmentTypes.forEach((appointment) => {
        appointmentMap[appointment.id] = appointment.title
      })
  
      // get an object mapping appointment name with array of id. e.g.:- {"medication" : [1,2], ...}
      const appointmentMapping = {}
  
      allAppointmentTypes.forEach((appointment) => {
        const { title, id } = appointment
        const label = categorizeAppointment(title)
  
        if (appointmentMapping[label]) {
          appointmentMapping[label].push(id)
        } else {
          appointmentMapping[label] = [id]
        }
      })
      setAppointmentsMap(appointmentMapping)
  
      //getting supported appointment type by provider
      const appointmentTypeUnique = new Set()
      if (provider?.appt_types?.length) {
        const appointmentTypes = []
        provider.appt_types.map((element) => {
          appointmentTypes.push(element.id)
          if (element.id) appointmentTypeUnique.add(categorizeAppointment(appointmentMap[element.id]))
        })

        form.setFieldValue('appt_types', [...appointmentTypeUnique])
      }
      
      // Set isAvailableForScheduling based on provider data
      setIsAvailableForScheduling(provider.available_for_scheduling || false)
      
      // Set profile image if available
      if (provider.profile_image_signed_url) {
        setImageUrl(provider.profile_image_signed_url)
      }
      
      setLoading(false)
    } catch (error) {
      console.error('Error loading clinician data:', error)
      setLoading(false)
      Modal.error({
        title: 'Error',
        content: 'Failed to load clinician data. Please try again later.'
      })
    }
  }

  const getAppointmentType = (appointments) => {
    const appointmentTypeArray = []
    if (appointments.length) {
      appointments.forEach((appointment) => {
        appointmentTypeArray.push(...appointmentsMap[appointment])
      })
    }
    return appointmentTypeArray
  }

  const onFinish = async (data) => {
    setLoading(true)
    const errors = validateForm(data)
    if (Object.keys(errors).length === 0) {
      try {
        // Convert checkbox values to boolean if they're not already
        data.available_for_scheduling = !!data.available_for_scheduling
        data.available_for_new_booking = !!data.available_for_new_booking
        data.available_for_followup_booking = !!data.available_for_followup_booking

        // Create FormData object
        const formData = new FormData()
        
        // Add form fields to FormData
        formData.append('provider_title', data.provider_title || '')
        formData.append('provider_bio', data.provider_bio || '')
        formData.append('doxy_link', data.doxy_link)
        formData.append('timezone', data.timezone)
        formData.append('search_priority', data.search_priority || 0)
        formData.append('appt_types', data.appt_types ? JSON.stringify(getAppointmentType(data.appt_types)) : JSON.stringify([]))
        
        formData.append('available_for_scheduling', data.available_for_scheduling)
        formData.append('available_for_new_booking', data.available_for_new_booking)
        formData.append('available_for_followup_booking', data.available_for_followup_booking)
        
        // Add AMD fields if they exist
        if (data.amd_column_id) formData.append('amd_column_id', data.amd_column_id)
        if (data.amd_column_heading) formData.append('amd_column_heading', data.amd_column_heading)
        
        // Include profile image if present and it's a file upload
        if (fileList && fileList.length > 0) {
          formData.append('profile_image', fileList[0])
        } else if (imageUrl) {
          // For existing image that was already uploaded or base64
          formData.append('profile_image', imageUrl)
        }
        
        // Create GHL contact, user, and calendar before saving clinician data
        let ghlSetupSuccessful = false;
        const initialPassword = generateRandomPassword()
        let userResponse = null;
        
        // 1. Create or find GHL contact
        try {
          const contactData = {
            by: 'email',
            from: 'complete-clinician-onboarding',
            email: data.lunajoy_email,
            clinicianData
          };
          const contactResponse = await findOrCreateGHLContact(contactData);
          
          // Store the GHL contact ID in the form data
          if (contactResponse && contactResponse.id) {
            formData.append('ghl_contact_id', contactResponse.id);
          }
        } catch (contactError) {
          console.error('GHL Contact Creation Error:', contactError);
          setLoading(false);
          Modal.error({
            title: 'GHL Integration Error',
            content: (
              <div>
                <p>Failed to create or find GHL contact for the clinician.</p>
                <p>Error: {contactError.message || 'Unknown error occurred'}</p>
                <p>Please try again or contact support if the issue persists.</p>
              </div>
            ),
            okText: 'Ok',
            cancelButtonProps: { style: { display: 'none' } }
          });
          return; // Stop execution and don't proceed with save
        }
        
        // 2. Create or find GHL user
        try {
          const userData = {
            firstName: clinicianData?.first_name,
            lastName: clinicianData?.last_name,
            email: data.lunajoy_email,
            password: initialPassword,
            phone: clinicianData?.phone,
          };
          userResponse = await findOrCreateGHLUser(userData);
          
          // Store the GHL user ID in the form data
          if (userResponse) {
            if (userResponse?.user?.id || userResponse?.id) {
              formData.append('ghl_user_id', userResponse?.user?.id || userResponse?.id);
              if (!userResponse?.isExisting) {
                formData.append('ghl_user_initial_password', initialPassword);
              }
            } else {
              throw new Error('Invalid user response - missing user ID');
            }
          } else {
            throw new Error('Failed to get user response from GHL');
          }
        } catch (userError) {
          console.error('GHL User Creation Error:', userError);
          setLoading(false);
          Modal.error({
            title: 'GHL User Creation Error',
            content: (
              <div>
                <p>Failed to create or find GHL user for the clinician.</p>
                <p>Error: {userError.message || 'Unknown error occurred'}</p>
                <p>Please try again or contact support if the issue persists.</p>
              </div>
            ),
            okText: 'Ok',
            cancelButtonProps: { style: { display: 'none' } }
          });
          return; // Stop execution and don't proceed with save
        }

        // 3. Create or find GHL calendar (only if user was created successfully)
        try {
          const ghlUserId = formData.get('ghl_user_id');
          if (!ghlUserId) {
            throw new Error('GHL User ID is missing - cannot create calendar');
          }
          
          const calendarData = {
            ghl_user_id: ghlUserId,
            name: `${clinicianData.first_name} ${clinicianData.last_name}`,
            doxy_link: data.doxy_link,
          };
          const calendarResponse = await findOrCreateGHLCalendar(calendarData);
          
          // Store the GHL calendar ID in the form data
          if (calendarResponse && calendarResponse.calendar && calendarResponse.calendar.id) {
            formData.append('ghl_calendar_id', calendarResponse.calendar.id);
            ghlSetupSuccessful = true;
          } else {
            throw new Error('Invalid calendar response - missing calendar ID');
          }
        } catch (calendarError) {
          console.error('GHL Calendar Creation Error:', calendarError);
          setLoading(false);
          Modal.error({
            title: 'GHL Calendar Creation Error',
            content: (
              <div>
                <p>Failed to create or find GHL calendar for the clinician.</p>
                <p>Error: {calendarError.message || 'Unknown error occurred'}</p>
                <p>Please try again or contact support if the issue persists.</p>
              </div>
            ),
            okText: 'Ok',
            cancelButtonProps: { style: { display: 'none' } }
          });
          return; // Stop execution and don't proceed with save
        }
        
        // Only proceed with saving clinician data if all GHL steps were successful
        if (!ghlSetupSuccessful) {
          setLoading(false);
          Modal.error({
            title: 'GHL Integration Failed',
            content: (
              <div>
                <p>GHL contact, user, and calendar were not created successfully.</p>
                <p>Please try again or contact support.</p>
              </div>
            ),
            okText: 'Ok',
            cancelButtonProps: { style: { display: 'none' } }
          });
          return;
        }
        
        // Complete the clinician onboarding process - send emails and move contact to next stage
        if (!userResponse?.isExisting) {
          try {
            const ghlDashboardLink = `https://ghl.hellolunajoy.com/v2/location/eChICUpjp3VmpOWTnker/opportunities/list`;
            const amdKeeperLink = data.amd_login_keeper_link
            const doxyKeeperLink = data.doxy_login_keeper_link
            const ghlCredentials = {
              username: data.lunajoy_email,
              password: initialPassword
            }
            const gmailKeeperLink = data.gmail_login_keeper_link
            
            // Prepare data for the complete onboarding endpoint
            const onboardingData = {
              firstName: clinicianData.first_name,
              lastName: clinicianData.last_name,
              lunajoyEmail: data.lunajoy_email,
              doxyKeeperLink: doxyKeeperLink,
              amdKeeperLink: amdKeeperLink,
              ghlCredentials: ghlCredentials,
              gmailKeeperLink: gmailKeeperLink,
              fromStageId: "38d695c9-246f-4155-9365-89d74eb2b80d",
              toStageId: "0a0c7a6a-15d4-4845-a83b-aa3228339ba4",
              ghlDashboardLink: ghlDashboardLink
            };
            
            await completeClinicianOnboarding(onboardingData);
            
            console.log("Clinician onboarding process completed successfully");
          } catch (onboardingError) {
            console.error('Clinician Onboarding Error:', onboardingError);
            // Note: We'll continue with saving clinician data even if this step fails
            // But we'll show a warning modal
            Modal.warning({
              title: 'Onboarding Notification Warning',
              content: (
                <div>
                  <p>The clinician record was created successfully, but there was an issue with sending the onboarding emails.</p>
                  <p>Error: {onboardingError.message || 'Unknown error'}</p>
                  <p>You may need to manually send the onboarding information to the clinician.</p>
                </div>
              ),
              okText: 'Continue',
            });
          }
        } else {
          console.info("Clinician already exists in GHL")
        }
        
        // Call the API to save the data
        await saveClinicianEmail(clinicianId, data.lunajoy_email)
        await saveClinicianData(clinicianId, formData)
        
        notify(`Clinician details completed successfully!`)
        setLoading(false)
        navigate('/clinicians-2.0')
      } catch (error) {
        console.error(error)
        setLoading(false)
        Modal.error({
          title: 'Error Message',
          content: (
            <div>
              <p>Oops! Failed to complete onboarding.</p>
              <p>{error.message}</p>
            </div>
          ),
          okText: 'Ok',
          cancelButtonProps: { style: { display: 'none' } }
        })
      }
    } else {
      setLoading(false)
      const errorMessages = Object.values(errors).map((error, index) => <div key={index}>* {error}</div>)
      Modal.error({
        title: 'Error Message',
        content: (
          <div>
            <p>Oops! Cannot complete onboarding due to the following errors:</p>
            {errorMessages}
          </div>
        ),
        okText: 'Ok',
        cancelButtonProps: { style: { display: 'none' } },
        onOk: async () => {
          console.log('Error in complete onboarding')
        }
      })
    }
  }

  const goBack = () => {
    navigate('/clinicians-2.0')
  }

  const handleImageUpload = async ({ file, onSuccess, onError }) => {
    if (file.size > 2 * 1024 * 1024) {
      onError('File size should be less than 2MB')
      setFileList([])
      Modal.confirm({
        title: 'Error',
        content: (
          <div>
            <p>File size should be less than 2 MB</p>
          </div>
        ),
        okText: 'Ok',
        cancelButtonProps: { style: { display: 'none' } }
      })
    } else {
      onSuccess('ok')
      setFileList([file])
    }
  }

  // Watch for changes in the available_for_scheduling field
  const handleFormValuesChange = (changedValues) => {
    if ('available_for_scheduling' in changedValues) {
      setIsAvailableForScheduling(changedValues.available_for_scheduling);
    }
  };

  // Add handler for image load
  const handleImageLoad = () => {
    setImageLoading(false)
  }
  
  // Add handler for image error
  const handleImageError = () => {
    setImageLoading(false)
  }
  
  // Reset image loading state when image URL changes
  useEffect(() => {
    if (imageUrl) {
      setImageLoading(true)
    }
  }, [imageUrl])

  return (
    <Spin spinning={loading}>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        width: '100%',
        padding: '0 16px'
      }}>
        <div style={{ 
          width: '100%',
          maxWidth: '1200px'
        }}>
          <h2 style={{ marginBottom: '24px', textAlign: 'center' }}>Complete Clinician Onboarding</h2>
          
          <Form 
            form={form} 
            onFinish={onFinish}
            onValuesChange={handleFormValuesChange}
            encType="multipart/form-data"
            layout="vertical"
            labelAlign="left"
            colon={false}
            requiredMark="optional"
          >
            <Card 
              title={clinicianName ? clinicianName : "Clinician Information"}
              style={{ 
                marginBottom: 24, 
                borderRadius: '8px', 
                border: '1px solid #d9d9d9',
                backgroundColor: '#f9f9ff'
              }}
              headStyle={{ 
                fontSize: '18px', 
                fontWeight: 'bold',
                backgroundColor: '#eceeff',
                borderTopLeftRadius: '8px',
                borderTopRightRadius: '8px',
                textAlign: 'center'
              }}
            >
              <Row gutter={24}>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="amd_details"
                    label="AMD Clinician (Search and select a clinician to link with this profile)"
                    rules={[
                      {
                        required: true,
                        message: 'Please select a clinician to link with this profile'
                      }
                    ]}
                  >
                    <Select
                      placeholder="Search clinician by name"
                      showSearch
                      loading={fetchingAmdColumns}
                      optionFilterProp="children"
                      filterOption={(input, option) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                      onChange={(value, option) => {
                        // When an option is selected, set both the column_id and column_heading in form data
                        form.setFieldsValue({
                          amd_column_id: option.data.column_id,
                          amd_column_heading: option.data.column_heading
                        });
                      }}
                      notFoundContent={
                        fetchingAmdColumns ? (
                          <div style={{ textAlign: 'center', padding: '8px' }}>
                            <Spin size="small" />
                            <div style={{ marginTop: '8px' }}>Loading clinicians...</div>
                          </div>
                        ) : (
                          <div style={{ textAlign: 'center', padding: '8px' }}>
                            No clinicians found
                          </div>
                        )
                      }
                    >
                      {amdColumns.map((column) => (
                        <Option 
                          key={column.column_id} 
                          value={column.column_id}
                          data={column} // Store full clinician data for access on selection
                        >
                          {column.first_name && column.last_name 
                            ? `${column.first_name} ${column.last_name}`
                            : column.column_heading || `Column ID: ${column.column_id}`}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                  {/* Hidden form items to store the selected column_id and column_heading */}
                  <Form.Item name="amd_column_id" hidden>
                    <Input />
                  </Form.Item>
                  <Form.Item name="amd_column_heading" hidden>
                    <Input />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="doxy_link"
                    label="Doxy Link"
                    rules={[
                      {
                        required: true,
                        message: 'Please enter doxy link.'
                      }
                    ]}
                  >
                    <Input placeholder="Enter doxy.me link" />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={24}>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="timezone"
                    label="Time Zone"
                    rules={[
                      {
                        required: true,
                        message: 'Please select time zone'
                      }
                    ]}
                  >
                    <Select placeholder="Select a time zone">
                      {dropdownOptions?.timezone?.map((time) => (
                        <Option key={time.value} value={time.value}>
                          {time.label}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="appt_types"
                    label="Appointment Types"
                    rules={[
                      {
                        required: true,
                        message: 'Please select at least one appointment type'
                      }
                    ]}
                  >
                    <Select
                      placeholder="Select appointment type(s)"
                      mode="multiple"
                      showSearch
                      optionFilterProp="children"
                      filterOption={(input, option) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                    >
                      {appointmentOptions.map((appointment) => (
                        <Option key={appointment} value={appointment}>
                          {appointment}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              {/* Only show login keeper link fields if provider doesn't have all required fields */}
              {!(clinicianData.doxy_link && clinicianData.provider_integration_map?.amd_column_id && clinicianData.provider_integration_map?.ghl_user_id) && (
                <>
                  <Row gutter={24}>
                    <Col xs={24} md={12}>
                      <Form.Item
                        name="gmail_login_keeper_link"
                        label="Gmail Login Keeper Link"
                        rules={[
                          {
                            required: true,
                            message: 'Please enter Gmail Login Keeper Link'
                          }
                        ]}
                      >
                        <Input placeholder="Enter Gmail Login Keeper Link" />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                      <Form.Item
                        name="amd_login_keeper_link"
                        label="AMD Login Keeper Link"
                        rules={[
                          {
                            required: true,
                            message: 'Please enter AMD Login Keeper Link'
                          }
                        ]}
                      >
                        <Input placeholder="Enter AMD Login Keeper Link" />
                      </Form.Item>
                    </Col>
                  </Row>
                </>
              )}

              <Row gutter={24}>
                {!(clinicianData.doxy_link && clinicianData.provider_integration_map?.amd_column_id && clinicianData.provider_integration_map?.ghl_user_id) && (
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="doxy_login_keeper_link"
                      label="Doxy Login Keeper Link"
                      rules={[
                        {
                          required: true,
                          message: 'Please enter Doxy Login Keeper Link'
                        }
                      ]}
                    >
                      <Input placeholder="Enter Doxy Login Keeper Link" />
                    </Form.Item>
                  </Col>
                )}
                <Col xs={24} md={12}>
                  <Form.Item
                    name="lunajoy_email"
                    label="Lunajoy Email"
                    rules={[
                      {
                        required: true,
                        message: 'Please enter Lunajoy email'
                      },
                      {
                        type: 'email',
                        message: 'Please enter a valid email'
                      }
                    ]}
                  >
                    <Input placeholder="Enter Lunajoy email" />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={24}>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="provider_title"
                    label="Provider Title"
                  >
                    <Input placeholder="Enter clinician title" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="search_priority"
                    label="Search Priority"
                  >
                    <InputNumber 
                      min={1} 
                      max={100} 
                      placeholder="Enter search priority (1-100)"
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                </Col>
              </Row>
              
              <Row gutter={24}>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="provider_bio"
                    label="Provider Bio"
                  >
                    <TextArea
                      placeholder="Enter provider bio"
                      autoSize={{ minRows: 2, maxRows: 8 }}
                      showCount
                      maxLength={500}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={6}>
                  <Form.Item
                    name="profile_image"
                    label="Profile Image"
                  > 
                    <Upload
                      maxCount={1}
                      fileList={fileList}
                      onRemove={(value) => {
                        setFileList([])
                      }}
                      customRequest={handleImageUpload}
                      listType="text"
                      accept=".jpg,.jpeg,.png,.webp,.avif"
                    >
                      <Button icon={<UploadOutlined />}>
                        {imageUrl ? 'Change Profile Image' : 'Upload Profile Image'}
                      </Button>
                      <div>
                        <small>The image size must be less than 2 MB</small>
                      </div>
                    </Upload>
                  </Form.Item>
                </Col>
                <Col xs={24} md={6} style={{ display: imageUrl ? 'block' : 'none' }}>
                  <div style={{ position: 'relative', minHeight: '95px' }}>
                    {imageLoading && (
                      <div style={{ 
                        position: 'absolute', 
                        top: 0, 
                        left: 0, 
                        width: '100%', 
                        height: '95px', 
                        display: 'flex', 
                        justifyContent: 'center', 
                        alignItems: 'center',
                        background: '#f0f0f0',
                        borderRadius: '4px',
                        border: '1px solid #d9d9d9'
                      }}>
                        <Spin size="small" />
                      </div>
                    )}
                    <img 
                      src={imageUrl} 
                      alt="Clinician profile" 
                      style={{ 
                        maxWidth: '100%', 
                        maxHeight: '95px', 
                        borderRadius: '4px',
                        border: '1px solid #d9d9d9',
                        display: imageLoading ? 'none' : 'block'
                      }} 
                      onLoad={handleImageLoad}
                      onError={handleImageError}
                    />
                  </div>
                </Col>
              </Row>
              
              <h3 style={{ 
                margin: '24px 0 16px 0', 
                fontWeight: 'bold', 
                color: '#333',
                borderTop: '1px solid #e8e8e8',
                paddingTop: '24px'
              }}>
                Booking Settings
              </h3>
              
              <Row gutter={24}>
                <Col xs={24} md={8}>
                  <Form.Item 
                    label="Available for Scheduling" 
                    name="available_for_scheduling" 
                    valuePropName="checked"
                    rules={[
                      {
                        required: true,
                        message: 'Please select if the clinician is available for scheduling.'
                      }
                    ]}
                  >
                    <Checkbox />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8} style={{ display: isAvailableForScheduling ? 'block' : 'none' }}>
                  <Form.Item 
                    label="Available for New Booking" 
                    name="available_for_new_booking" 
                    valuePropName="checked"
                    rules={[
                      {
                        required: true,
                        message: 'Please select if the clinician is available for new bookings.'
                      }
                    ]}
                  >
                    <Checkbox />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8} style={{ display: isAvailableForScheduling ? 'block' : 'none' }}>
                  <Form.Item 
                    label="Available for Follow-up Booking" 
                    name="available_for_followup_booking" 
                    valuePropName="checked"
                    rules={[
                      {
                        required: true,
                        message: 'Please select if the clinician is available for follow-up bookings.'
                      }
                    ]}
                  >
                    <Checkbox />
                  </Form.Item>
                </Col>
              </Row>
            </Card>

            {/* Back and Submit Buttons */}
            <Row>
              <Col xs={24} style={{ textAlign: 'right', marginBottom: 40 }}>
                <Button size="large" onClick={goBack} style={{ marginRight: 10, fontWeight: 'semibold' }}>Back</Button>
                <Button 
                  size="large" 
                  type="primary" 
                  htmlType="submit" 
                  style={{ padding: '0 30px', fontWeight: 'semibold' }}
                >
                  Save Changes
                </Button>
              </Col>
            </Row>
          </Form>
        </div>
      </div>
    </Spin>
  )
}

export default CompleteClinicianOnboarding 